<template>
  <ValidationObserver
    v-slot="{ errors, failed }"
    ref="observer"
    tag="div"
    class="my-2 ranked-table"
    slim
  >
    <label
      class="text-body-1 font-weight-medium"
      :class="{ 'error--text': failed, disabled: disabled }"
    >
      {{ tableLabel + ` ${required || failed ? '' : ' - Optional'}` }}
    </label>
    <p v-if="failed" class="error--text mb-0 mt-0">{{
      displayError(errors)
    }}</p>
    <p v-else class="mb-0 mt-0">{{ tableNote }}</p>

    <v-btn class="mt-2" x-small plain @click="clearSelections">
      <base-icon class="mr-1" name="times-circle"></base-icon>
      Clear Selections
    </v-btn>
    <validation-provider
      v-for="(rowOption, rIndex) in customRowOptions"
      :key="rowOption.Value"
      v-slot="{ failed: vpFailed }"
      :name="rowOption.Value"
      :rules="
        required && rowOption.Value !== 'HeaderRowForDisplay' ? 'required' : ''
      "
      slim
    >
      <v-radio-group
        :key="rowOption.Value"
        :value="innerValue[rowOption.Value]"
        :row="$vuetify.breakpoint.mdAndUp"
        :disabled="disabled"
        :hide-details="true"
        class="ranked-row ma-0 pa-3"
        :class="{ 'ranked-row-striped': $vuetify.breakpoint.mdAndUp }"
        :error="vpFailed"
        :background-color="$vuetify.breakpoint.mdAndUp ? '' : 'grey lighten-2'"
        @change="handleInputChange(rowOption.Value, $event)"
      >
        <template v-if="$vuetify.breakpoint.mdAndUp" v-slot>
          <div class="mr-auto d-flex" style="flex: 1">
            <template v-if="rIndex !== 0">
              <base-icon
                v-show="vpFailed"
                class="mr-1"
                name="exclamation-triangle"
              ></base-icon>
              <div>{{ rowOption.Text }}</div>
            </template>
          </div>
          <div
            v-for="columnOption in customColumnOptions"
            :key="rowOption.Value + '_' + columnOption.Value"
            class="d-flex align-center justify-center ranked-row-column px-2"
          >
            <v-radio v-if="rIndex !== 0" :value="columnOption.Value"> </v-radio>
            <div
              class="subtitle-2"
              :class="{ 'ranked-row-column-selection': rIndex !== 0 }"
              >{{ columnOption.Text }}</div
            >
          </div>
        </template>

        <template v-else v-slot>
          <div class="px-4 py-3">
            <base-icon
              v-show="vpFailed"
              class="mr-1"
              name="exclamation-triangle"
            ></base-icon>
            {{ rowOption.Text }}
          </div>
          <v-divider class="pb-3"></v-divider>
          <v-radio
            v-for="(columnOption, cIndex) in customColumnOptions"
            :key="rowOption.Value + '_' + columnOption.Value"
            :value="columnOption.Value"
            :label="columnOption.Text"
            class="px-4"
            :class="{ 'pb-3': cIndex === customColumnOptions.length - 1 }"
          >
          </v-radio>
        </template>
      </v-radio-group>
    </validation-provider>
  </ValidationObserver>
</template>

<script>
export default {
  props: {
    value: {
      type: null,
      required: true,
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    tableLabel: {
      type: String,
      required: true,
    },
    tableNote: {
      type: String,
      required: false,
      default: '',
    },
    columnOptions: {
      type: Array,
      required: true,
    },
    rowOptions: {
      type: Array,
      required: true,
    },
    useNA: {
      type: Boolean,
      default: false,
    },
    serverErrors: {
      type: Object,
      default() {
        return {}
      },
    },
  },
  computed: {
    innerValue() {
      const innerValue = {}
      for (let i = 0; i < this.rowOptions.length; i++) {
        const propName = this.rowOptions[i].Value
        innerValue[propName] = this.value?.[propName] ?? null
      }

      return innerValue
    },
    customColumnOptions() {
      const options = [...this.columnOptions]

      if (this.useNA) {
        options.push({
          Text: 'N/A',
          Value: '___NA___',
        })
      }

      return options
    },
    customRowOptions() {
      const options = [...this.rowOptions]

      if (this.$vuetify.breakpoint.mdAndUp) {
        options.unshift({ IsHeader: true, Value: 'HeaderRowForDisplay' })
      }
      return options
    },
  },
  watch: {
    serverErrors(val) {
      this.$refs.observer.setErrors(val)
    },
  },
  methods: {
    handleInputChange(propName, val) {
      const answer = this.innerValue
      answer[propName] = val
      this.$emit('input', answer)
    },
    resetObserver(invalid) {
      if (invalid) return this.$refs.observer.reset()
    },
    clearSelections() {
      this.$emit('input', null)
    },
    displayError(errors) {
      const errorsToArray = Object.values(errors).flatMap((x) => x)
      return errorsToArray
        .filter((error, index) => {
          return errorsToArray.indexOf(error) === index
        })
        .toString()
    },
  },
}
</script>

<style scoped="true">
@media only screen and (max-width: 1264px) {
  .selected {
    background: rgba(0, 0, 0, 0.5);
    color: #fff;
  }

  div.disabled {
    opacity: 0.5;
  }

  label.disabled {
    opacity: 0.5;
  }
}
.ranked-row.ranked-row-striped:nth-of-type(even) {
  background-color: rgba(34, 34, 34, 0.05);
}

div.ranked-row-column-selection {
  visibility: hidden;
}
</style>


<style>
div.ranked-row > div.v-input__control > div.v-input__slot {
  border-radius: 8px !important;
}

div.ranked-row div.ranked-row-column > div.v-radio {
  position: absolute;
  margin-right: 0px;
}

div.ranked-row-column > div.v-radio > div.v-input--selection-controls__input {
  margin-right: 0px;
}
</style>
